import { Box, IconButton, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PUBLIC_SECCIONES_EN_FILA, type PublicShell } from '@ahora-patagonia/contracts';
import { IconoBuscar, IconoMenu } from '../modules/shared/components/iconos.js';
import { useCabeceraOculta } from './useCabeceraOculta.js';

export interface PublicHeaderProps {
  shell: PublicShell | null;
  onAbrirNav: () => void;
}

/**
 * El tope de secciones en la fila vive en los contracts (`PUBLIC_SECCIONES_EN_FILA`) porque el panel
 * lo necesita para avisarle a la redacción que lo que marque de más va a terminar en "Más".
 *
 * Bajó de 7 a 6 cuando el nombre del medio creció a 28 px: el ancho es el mismo y el título se
 * lleva una parte. El tope es fijo por corte a propósito (ver abajo), así que si no se ajusta ahí,
 * la séptima sección queda cortada al medio por el `overflow: hidden` del `nav`.
 */

/**
 * La cabecera del diario: 64 px, marca white-label y navegación.
 *
 * - **No queda fija**: se esconde al bajar y vuelve al primer gesto hacia arriba
 *   (`useCabeceraOculta` + `transform`, transición de 160 ms). En un teléfono cada píxel
 *   fijo es lectura que se pierde.
 * - La caja de marca mide 44×44 (34 en escritorio) con `object-fit: contain`: un logo
 *   apaisado entra reducido, uno alto deja aire, y **sin `logoUrl` queda solo el nombre**.
 *   El color del cliente vive en el token, no en el archivo.
 * - **El nombre del medio manda sobre el logo**, no al revés: es el que dice de qué diario es
 *   esta página, y el que sigue estando cuando el medio no cargó logo. Antes el logo medía el
 *   doble que el texto y la marca se leía como un ícono con una etiqueta al lado.
 * - En teléfono y tablet la navegación es el cajón (hamburguesa); en escritorio (≥1200) hay
 *   fila fija con las secciones que la redacción eligió y "Más" abre el cajón con la lista
 *   completa. El tope es fijo por corte a propósito: medir con JS causaría un salto al hidratar.
 * - **La fila la cura la redacción** (`enElHeader`, desde "Orden de la página"). Antes entraban
 *   todas las categorías y las que no cabían quedaban escondidas por el corte, sin que nadie
 *   eligiera cuáles. Ahora el corte se aplica a lo elegido.
 */
export function PublicHeader({ shell, onAbrirNav }: PublicHeaderProps) {
  const oculta = useCabeceraOculta();
  const secciones = shell?.nav ?? [];
  const elegidas = secciones.filter((s) => s.enElHeader);
  const enFila = elegidas.slice(0, PUBLIC_SECCIONES_EN_FILA);

  /*
   * "Más" existe para una sola cosa: avisar que hay secciones que la redacción **dejó afuera** de la
   * fila. Dos casos donde no hay nada que avisar y antes se dibujaba igual:
   *
   * - **Están todas marcadas.** No hay ninguna afuera, así que "Más" prometía una lista que no
   *   agregaba nada a lo que ya se ve. (Si además son más de las que entran en la fila, el corte se
   *   las lleva y quedan en la hamburguesa; el panel lo avisa al marcarlas.)
   * - **No hay ninguna marcada.** La fila queda vacía y "Más" era lo único ahí: se leía como una
   *   sección llamada "Más", pegado a la marca, al lado de una hamburguesa que abre exactamente el
   *   mismo cajón.
   *
   * En los dos casos el cajón sigue a un clic de distancia: la hamburguesa está siempre, también en
   * escritorio, así que sacar "Más" no deja nada inalcanzable.
   */
  const hayMas = enFila.length > 0 && elegidas.length < secciones.length;

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (t) => t.zIndex.appBar,
        // Cabecera en el color del cliente: el texto y los glifos heredan `--sobre-cliente`
        // (blanco o tinta según la luminancia de la marca), así que un tenant claro también
        // queda legible. Los enlaces se traen a `inherit` porque el theme los pinta en tinta
        // y su hover en el color del cliente, que acá sería el mismo del fondo.
        backgroundColor: 'var(--color-cliente)',
        color: 'var(--sobre-cliente)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.18)',
        '& a': { color: 'inherit' },
        '& a:hover': { color: 'inherit' },
        '& .MuiIconButton-root': { color: 'inherit' },
        '& .MuiIconButton-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.14)' },
        transform: oculta ? 'translateY(-100%)' : 'none',
        transition: 'transform 160ms ease',
      }}
    >
      {/*
        A todo el ancho, **sin** el tope de 1440 que sí tiene el cuerpo de la página: en pantallas
        anchas la barra quedaba con dos franjas del color del medio vacías a los costados, con el
        logo y el buscador flotando en el medio. La cabecera es una barra de navegación, no una
        columna de lectura, así que se estira hasta el borde y los extremos quedan anclados ahí.

        Consecuencia asumida: arriba de 1440 el logo ya no se alinea con la primera columna del
        contenido. Es el precio de que la barra se lea como barra.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 64,
          px: { xs: '14px', sm: '20px', lg: '32px' },
        }}
      >
        <IconButton aria-label="Abrir la navegación" onClick={onAbrirNav} edge="start">
          <IconoMenu />
        </IconButton>

        <MuiLink
          component={RouterLink}
          to="/"
          aria-label={shell ? `${shell.tenant.nombre} — portada` : 'Portada'}
          // `minWidth: 0` y sin `flexShrink: 0`: la marca tiene que poder encogerse y cortar con
          // puntos suspensivos. Fijarla empujaría el buscador fuera de la pantalla en un teléfono
          // angosto con un nombre largo — y ahí la navegación ni siquiera está, así que no hay
          // nada que ganar.
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}
        >
          {shell?.tenant.logoUrl && (
            <Box
              component="img"
              src={shell.tenant.logoUrl}
              alt=""
              sx={{
                // Cerca de la altura de la mayúscula del nombre, no de la fila entera: así el
                // logo acompaña al título en vez de competirle.
                width: { xs: 44, lg: 34 },
                height: { xs: 44, lg: 34 },
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: { xs: 21, lg: 28 },
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              // En `ch`, así el tope acompaña al cuerpo: el mismo número de caracteres entra a
              // 21 px que a 28. Un tope en px recortaría el nombre justo en el corte donde el
              // texto creció, que es donde menos hace falta.
              maxWidth: '22ch',
            }}
          >
            {shell?.tenant.nombre ?? ''}
          </Typography>
        </MuiLink>

        <Box
          component="nav"
          aria-label="Secciones"
          sx={{
            display: { xs: 'none', lg: 'flex' },
            alignItems: 'center',
            gap: 2.5,
            // 32 px de aire entre la marca y la primera sección: con el título a 28 px, 16 px
            // hacían que "Ahora Patagonia" y "POLÍTICA" se leyeran como una sola tira.
            ml: 4,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {enFila.map((s) => (
            <MuiLink
              key={s.key}
              component={RouterLink}
              to={s.path}
              sx={{
                fontWeight: 800,
                fontSize: 14,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                minHeight: 48,
              }}
            >
              {s.name}
            </MuiLink>
          ))}
          {hayMas && (
            <Box
              component="button"
              type="button"
              onClick={onAbrirNav}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 14,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'inherit',
                opacity: 0.82,
                display: 'flex',
                alignItems: 'center',
                minHeight: 48,
              }}
            >
              Más
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        <IconButton
          component={RouterLink}
          to="/buscar"
          aria-label="Buscar"
          edge="end"
        >
          <IconoBuscar />
        </IconButton>
      </Box>
    </Box>
  );
}
